export const DEFAULT_PRECISION = 2;

const STRING_TO_NUMBER_REGEX = /[^0-9.-]+/g;

export const countDecimal = (num: number) => {
  if (!num || (Math.floor(num) === num)) return 0;

  return num.toString().split('.')[1].length || 0;
};

export const ICON_COMPARATOR_MAP: { [key: string]: string } = {
  equal: '=',
  greater_than: '>',
  less_than: '<'
};

export const compareNumbers = (numA: string, comparatorString: string, numB: string) => {
  const a = Number.parseFloat(numA);
  const b = Number.parseFloat(numB);

  if (Number.isNaN(a) || Number.isNaN(b)) return false;

  if (comparatorString === 'less_than') {
    return a < b;
  } if (comparatorString === 'greater_than') {
    return a > b;
  }

  return a === b;
};

/**
 * This handles reformatting currency strings with a single decimal point to a number with a fixed number of decimal.
 * @param {string} str String upon which to convert, can be '$ 12,323.2', '$123.20', '$123', '123.2', '123, '123.4002'
 * @param {number} digits Number of digits to have after conversion.
 * @returns {number} A number formatted with the provided number of digits or a 0.00 float.
 */
export const formatStringAsNumber = (str: string, digits = 4) => {
  if (!str || typeof str !== 'string') return 0.00;
  const newString = str.replace(STRING_TO_NUMBER_REGEX, '');

  const newValue = Number.parseFloat(newString).toFixed(digits);

  return Number.isNaN(newValue) ? 0.00 : newValue;
};

export const roundToPrecision = (value: number, precision: number): number => {
  const scale = 10 ** precision;
  return Math.round(value * scale) / scale;
};

export const isOnlyNumbers = (str: string) => /^\d+$/.test(str);
export const isOnlyNumberOrEmptyString = (str: string) => /^\s*\d*\s*$/.test(str);

/**
 * If decimals can be shown, show up to provided amount of decimals places, otherwise
 * render as a whole number and not with ".00" appended.
 * @param {float|number|string} num
 * @param {number} precision
 * @returns {float|string} Returns a string of fixed precision if precision specified, otherwise returns an integer. If
 * parsing the value produces NaN, returns a 0.
 */
export const withConditionalDecimals = (num: string, precision = DEFAULT_PRECISION) => {
  let parsed = Number.parseFloat(num);

  if (!parsed) parsed = 0;
  return parsed % 1 ? parsed.toFixed(precision) : parsed;
};

export const isValidNumber = (value: any): boolean => {
  if (typeof value === 'number' && !Number.isNaN(value)) return true;
  if (typeof value !== 'string') return false;
  return !Number.isNaN(Number(value));
};

/**
 * Safely parse a value to a number with fallback
 * Handles strings, numbers, null, undefined, and NaN cases
 *
 * @param value - Value to parse (string | number | null | undefined)
 * @param defaultValue - Fallback value if parsing fails (default: 0)
 * @returns Parsed number or default value
 *
 * @example
 * safeParseNumber('123.45') // 123.45
 * safeParseNumber(123.45) // 123.45
 * safeParseNumber('abc', 0) // 0
 * safeParseNumber(null, 0) // 0
 * safeParseNumber(undefined, 1) // 1
 */
export const safeParseNumber = (
  value: number | string | null | undefined,
  defaultValue = 0
): number => {
  // Handle null/undefined early
  if (value == null) return defaultValue;

  // If already a number, return it (unless NaN)
  if (typeof value === 'number') {
    return Number.isNaN(value) ? defaultValue : value;
  }

  // Parse string
  if (typeof value === 'string') {
    // Empty string should return default
    if (value.trim() === '') return defaultValue;

    const parsed = Number.parseFloat(value);
    return Number.isNaN(parsed) ? defaultValue : parsed;
  }

  // Unexpected type, return default
  return defaultValue;
};
